import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { equipamentoService } from '../../services/equipamentoService';
import type { Equipamento, EventoHistoricoAtivo, StatusCustodiaEquipamento } from '../../models/equipamento.types';

export const SI_EQUIPAMENTOS_QUERY_KEYS = {
  all: ['si', 'equipamentos'] as const,
  detalhe: (id: string) => [...SI_EQUIPAMENTOS_QUERY_KEYS.all, 'detalhe', id] as const,
  porPatrimonio: (patrimonio: string) =>
    [...SI_EQUIPAMENTOS_QUERY_KEYS.all, 'por-patrimonio', patrimonio] as const,
  lista: (statusCustodia?: StatusCustodiaEquipamento) =>
    [...SI_EQUIPAMENTOS_QUERY_KEYS.all, 'lista', statusCustodia ?? 'todos'] as const,
};

/**
 * Tempo real (Firestore `onSnapshot`), não busca única — `statusCustodia` desse
 * equipamento é atualizado por Cloud Functions (onMovimentacaoWriteSyncEquipamentoCustody/
 * onEstadiaClienteWriteSyncEquipamentoCustody), não pelo próprio client. Com busca única, o
 * `invalidateQueries` disparado no client roda antes da Cloud Function terminar de
 * escrever, e a aba "Custódia" mostrava o status antigo até um refresh manual acontecer
 * por acaso depois — o listener elimina essa corrida, mostrando o valor assim que o
 * Firestore realmente commita, venha a escrita do client ou de uma Cloud Function.
 */
function useEquipamentosTempoReal(statusCustodia: StatusCustodiaEquipamento | undefined, excluidos: boolean) {
  const [data, setData] = useState<Equipamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const restricoes = statusCustodia ? [where('statusCustodia', '==', statusCustodia)] : [];
    const unsub = onSnapshot(
      query(collection(db, 'si_equipamentos'), ...restricoes),
      (snap) => {
        setData(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }) as Equipamento)
            .filter((e) => Boolean(e.excluido) === excluidos)
        );
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
    return unsub;
  }, [statusCustodia, excluidos]);

  return { data, isLoading };
}

/** Ativos (sem os excluídos — ver Equipamento.excluido). */
export function useEquipamentosQuery(statusCustodia?: StatusCustodiaEquipamento) {
  return useEquipamentosTempoReal(statusCustodia, false);
}

/** Só os excluídos — pra seção "Excluídos" (ver e restaurar). */
export function useEquipamentosExcluidosQuery() {
  return useEquipamentosTempoReal(undefined, true);
}

/** Histórico do ativo em tempo real (mais recente primeiro) — o evento é gravado por Cloud
 * Function logo depois da escrita; com listener ele aparece sem precisar recarregar. */
export function useHistoricoEquipamentoQuery(equipamentoId: string | undefined) {
  const [data, setData] = useState<EventoHistoricoAtivo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setErro(null);
    if (!equipamentoId) {
      setData([]);
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'si_equipamentos', equipamentoId, 'historico'), orderBy('dataEvento', 'desc')),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EventoHistoricoAtivo));
        setIsLoading(false);
      },
      // Sem isso, falta de permissão (regra do histórico não publicada) parecia "nenhum evento".
      (error) => {
        setErro(error.code === 'permission-denied' ? 'sem-permissao' : error.message);
        setIsLoading(false);
      }
    );
    return unsub;
  }, [equipamentoId]);

  return { data, isLoading, erro };
}

export function useEquipamentoQuery(id: string | undefined) {
  return useQuery({
    queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.detalhe(id ?? ''),
    queryFn: () => equipamentoService.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useEquipamentoPorPatrimonioQuery(patrimonio: string | undefined) {
  return useQuery({
    queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.porPatrimonio(patrimonio ?? ''),
    queryFn: () => equipamentoService.getByPatrimonio(patrimonio as string),
    enabled: Boolean(patrimonio),
  });
}
